"use client";

import { submitServiceEnquiryForm } from "@/actions/frontEndActions/action";
import { useErrorToast, useSuccessToast } from "@/lib/hooks";
import { ServiceEnquirySchema } from "@/schemas/pagesSchema/enquiries/serviceEnquirySchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { BackgroundRevealButton, FontResizerWrapper } from "../..";
import ROUTES from "@/constants/routes";
import Link from "next/link";
import COUNTRY_CODES from "../../../../app/assets/jsonData/country.code.json";
import PhoneNumberInput from "../../common/PhoneNumberInput";

const ServicesEnquiryForm = ({
  currentLanguage = "en",
  formHeading = "",
  formDescription = "",
  services = [],
}) => {
  const [isThankYouShown, setIsThankYouShown] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setError,
    setValue,
  } = useForm({
    resolver: zodResolver(ServiceEnquirySchema),
    defaultValues: {
      name: "",
      subject: "",
      email: "",
      country_code: "",
      phoneNumber: "",
      service_name: "",
      message: "",
    },
  });

  const translate = useTranslations();

  // Handle Form Submit Functionality
  const onSubmit = async (data) => {
    const formData = watch();
    const response = await submitServiceEnquiryForm(formData, currentLanguage);

    if (response.success) {
      useSuccessToast(response?.message || "Content created successfully.");
      reset();
      setIsThankYouShown(true);
    } else {
      console.log("Email Response: ", response); // TODO Remove --
      useErrorToast(response?.message || "Something went wrong.");
    }
  };

  return (
    <div className="w-full mt-[80px] mb-[100px] p-5 md:p-10 bg-[#ECF6FE] rounded-[24px] border border-[#00587E33] shadow-[#00000040]">
      {isThankYouShown ? (
        <div className="relative w-full flex flex-col justify-center items-center mt-5">
          <div className="w-full h-full flex flex-col justify-center items-center gap-5">
            <h2 className="subtitle-1 md:heading-2 ltr:font-family-primary rtl:font-family-arabic text-primary-400 font-[600]">
              {currentLanguage === "en"
                ? "Thank you for your enquiry"
                : "شكرًا لطلبك"}
            </h2>
            <p className="text-gray-400 body1 ltr:font-family-secondary rtl:font-family-arabic text-center mb-4 px-3">
              {currentLanguage === "en"
                ? `Your enquiry has been successfully submitted to Fujairah Freezone Authority. Our team will review your request and get back to shortly with further details.`
                : "تم تقديم استفسارك بنجاح إلى هيئة المنطقة الحرة في الفجيرة. سيقوم فريقنا بمراجعة طلبك والتواصل معك قريبًا لتزويدك بالمزيد من التفاصيل."}
            </p>

            <BackgroundRevealButton
              isButton={true}
              buttonType="submit"
              buttonOnlickFnc={() => setIsThankYouShown(false)}
              btnLabel={
                currentLanguage === "en"
                  ? "Submit Another Enquiry"
                  : "إرسال طلب آخر"
              }
              textColor="text-gray-400 body1"
              className="w-fit"
            />
          </div>
        </div>
      ) : (
        <>
          {formHeading && (
            <FontResizerWrapper
              as="h2"
              fontClasses={{
                0: "subtitle-0-1 md:heading-0-3",
                1: "subtitle-1 md:heading-3",
                2: "subtitle-1-1 md:heading-3-1",
              }}
              className="text-gray-400 ltr:font-family-primary rtl:font-family-arabic font-[600] capitalize mb-5"
            >
              {formHeading}
            </FontResizerWrapper>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-5 mt-5">
              {/* Company Name */}
              <div className="w-full">
                <FontResizerWrapper
                  as="label"
                  fontClasses={{
                    0: "body-0-1",
                    1: "body1",
                    2: "body-1-1",
                  }}
                  className="text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[400]"
                >
                  {`${translate("Name")} *`}
                </FontResizerWrapper>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder={translate("Your Name")}
                      className="w-full h-[50px] border border-[#005B7E70] bg-transparent rounded-[12px] p-[16px] md:p-[20px] text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[500] mt-2"
                    />
                  )}
                />
                {errors && errors.name && (
                  <p className="text-red-500 text-[13px] ltr:font-family-secondary rtl:font-family-arabic mt-2">
                    {errors.name.message?.[currentLanguage] || "N/A"}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
              {/* Type Of Service */}
              <div className="w-full">
                <FontResizerWrapper
                  as="label"
                  fontClasses={{
                    0: "body-0-1",
                    1: "body1",
                    2: "body-1-1",
                  }}
                  className="text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[400]"
                >
                  {`${translate("Type of Services")} *`}
                </FontResizerWrapper>
                <Controller
                  name="service_name"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="w-full h-[50px] border border-[#005B7E70] bg-transparent rounded-[12px] px-[16px] md:px-[20px] text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[500] mt-2"
                    >
                      <option value="">
                        {currentLanguage === "en"
                          ? "Select a Service"
                          : "اختر خدمة"}
                      </option>
                      {services?.length > 0 &&
                        services?.map(
                          (item) =>
                            item?._id &&
                            item?.service_name?.[currentLanguage] && (
                              <option
                                key={item._id}
                                value={item.service_name[currentLanguage]}
                              >
                                {item.service_name[currentLanguage]}
                              </option>
                            )
                        )}
                      <option
                        value={currentLanguage === "en" ? "Other" : "أخرى"}
                      >
                        {currentLanguage === "en" ? "Other" : "أخرى"}
                      </option>
                    </select>
                  )}
                />
                {errors && errors.service_name && (
                  <p className="text-red-500 text-[13px] ltr:font-family-secondary rtl:font-family-arabic mt-2">
                    {errors.service_name.message?.[currentLanguage] || "N/A"}
                  </p>
                )}
              </div>

              {/* Subject */}
              <div className="w-full">
                <FontResizerWrapper
                  as="label"
                  fontClasses={{
                    0: "body-0-1",
                    1: "body1",
                    2: "body-1-1",
                  }}
                  className="text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[400]"
                >
                  {`${translate("Subject")}`}
                </FontResizerWrapper>
                <Controller
                  name="subject"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder={translate("Subject")}
                      className="w-full h-[50px] border border-[#005B7E70] bg-transparent rounded-[12px] p-[16px] md:p-[20px] text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[500] mt-2"
                    />
                  )}
                />
                {errors && errors.subject && (
                  <p className="text-red-500 text-[13px] ltr:font-family-secondary rtl:font-family-arabic mt-2">
                    {errors.subject.message?.[currentLanguage] || "N/A"}
                  </p>
                )}
              </div>
            </div>

            {/* Email & Phone */}
            <div className="w-full flex flex-col sm:flex-row gap-5 mt-5">
              <div className="w-full">
                <FontResizerWrapper
                  as="label"
                  fontClasses={{
                    0: "body-0-1",
                    1: "body1",
                    2: "body-1-1",
                  }}
                  className="text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[400]"
                >
                  {`${translate("Email")} *`}
                </FontResizerWrapper>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="email"
                      placeholder={translate("Your Email")}
                      className="w-full h-[50px] border border-[#005B7E70] bg-transparent rounded-[12px] p-[16px] md:p-[20px] text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[500] mt-2"
                    />
                  )}
                />
                {errors && errors.email && (
                  <p className="text-red-500 text-[13px] ltr:font-family-secondary rtl:font-family-arabic mt-2">
                    {errors.email.message?.[currentLanguage] || "N/A"}
                  </p>
                )}
              </div>

              {/* Phone Number */}
              <PhoneNumberInput
                control={control}
                errors={errors}
                label="Phone Number"
                currentLanguage={currentLanguage}
              />
            </div>

            {/* Message */}
            <div className="w-full mt-5">
              <FontResizerWrapper
                as="label"
                fontClasses={{
                  0: "body-0-1",
                  1: "body1",
                  2: "body-1-1",
                }}
                className="text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[400]"
              >
                {`${translate("Message")}`}
              </FontResizerWrapper>
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    rows={5}
                    type="text"
                    placeholder={translate("Your Message")}
                    className="w-full border border-[#005B7E70] bg-transparent rounded-[12px] p-[16px] md:p-[20px] text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[500] mt-2 mb-5"
                  />
                )}
              />
            </div>

            {formDescription && (
              <FontResizerWrapper
                as="p"
                fontClasses={{
                  0: "body-0-3",
                  1: "body3",
                  2: "body-3-1",
                }}
                className="text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[600] mb-5 mt-5"
              >
                {formDescription}
              </FontResizerWrapper>
            )}

            <FontResizerWrapper
              as="p"
              fontClasses={{
                0: "body-0-3",
                1: "body3",
                2: "body-3-1",
              }}
              className="text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[600] my-5"
            >
              {translate("form_extra_text")}{" "}
              <Link
                href={`${process.env.NEXT_PUBLIC_DOMAIN_URL}/${currentLanguage}${ROUTES.PRIVACY_POLICY}`}
                className="text-primary-400 underline"
              >
                {translate("privacy policy")}
              </Link>
            </FontResizerWrapper>

            {/* <div className="w-full mt-5">
        <FontResizerWrapper
          as="label"
          fontClasses={{
            0: "body-0-2",
            1: "body2",
            2: "body-2-1",
          }}
          className="text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[400] flex flex-col md:flex-row items-start md:items-center gap-2"
        >
          <span>{translate("Type of Services")}</span>
          <span>*</span>
        </FontResizerWrapper>

        <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5 mt-2">
          <Controller
            name="service_type"
            control={control}
            render={({ field }) => (
              <>
                <div className="flex items-center gap-2">
                  <input
                    id="renew-license"
                    type="radio"
                    checked={field.value === "renew-license"}
                    onChange={() => field.onChange("renew-license")}
                    className="w-[20px] h-[20px] border border-[#005B7E70] dark:border-[#FFFFFFB2] bg-transparent rounded-[4px] accent-[#005B7E] cursor-pointer"
                  />
                  <FontResizerWrapper
                    as="label"
                    fontClasses={{
                      0: "body-0-1",
                      1: "body1",
                      2: "body-1-1",
                    }}
                    className="text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[400]"
                  >
                    {`${translate("Renew License")}`}
                  </FontResizerWrapper>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    id="change-license"
                    type="radio"
                    checked={field.value === "change-license"}
                    onChange={() => field.onChange("change-license")}
                    className="w-[20px] h-[20px] border border-[#005B7E70] dark:border-[#FFFFFFB2] bg-transparent rounded-[4px] accent-[#005B7E] cursor-pointer"
                  />
                  <FontResizerWrapper
                    as="label"
                    fontClasses={{
                      0: "body-0-1",
                      1: "body1",
                      2: "body-1-1",
                    }}
                    className="text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[400]"
                  >
                    {`${translate("Change License")}`}
                  </FontResizerWrapper>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    id="cancel-license"
                    type="radio"
                    checked={field.value === "cancel-license"}
                    onChange={() => field.onChange("cancel-license")}
                    className="w-[20px] h-[20px] border border-[#005B7E70] dark:border-[#FFFFFFB2] bg-transparent rounded-[4px] accent-[#005B7E] cursor-pointer"
                  />
                  <FontResizerWrapper
                    as="label"
                    fontClasses={{
                      0: "body-0-1",
                      1: "body1",
                      2: "body-1-1",
                    }}
                    className="text-gray-400 ltr:font-family-secondary rtl:font-family-arabic font-[400]"
                  >
                    {`${translate("Cancel License")}`}
                  </FontResizerWrapper>
                </div>
              </>
            )}
          />
        </div>
      </div> */}

            {/* <FENormalUploadInputBox
        labelName={transalate("Upload a Request Letter")}
        placeholder={transalate("Upload")}
        required={true}
        className="mt-5"
      /> */}

            <div className="mt-10">
              <BackgroundRevealButton
                isButton={true}
                buttonType="submit"
                btnLabel={
                  isSubmitting
                    ? currentLanguage === "en"
                      ? "Loading..."
                      : "جارٍ التحميل..."
                    : currentLanguage === "en"
                      ? "Submit"
                      : "إرسال"
                }
                textColor="text-gray-400 body1"
                className="w-fit"
              />
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default ServicesEnquiryForm;
